<template>
  <div class="opportunity-header">
    <button @click="backToOpportunities" class="opportunity-header__back">
      <BaseIcon class="tender-header__back-icon" name="arrow-left" />
      <span class="tender-header__back-text"> {{ $t('menu.opportunities') }} </span>
    </button>
    <div class="opportunity-header__name">
      <OpportunityStatusType
        v-if="opportunity && opportunity.status"
        class="opportunity-header__status-type"
        :opportunity-status="opportunity.status"
        condensed
      />
      <div v-if="opportunity && opportunity.name" class="opportunity-header__title">
        {{ opportunity.name }}
      </div>
      <div v-if="opportunity && opportunity.status" class="opportunity-header__status">
        <BaseStatusMenu
          ref="opportunityStatusMenu"
          :items="opportunityStatuses"
          :current-status="opportunity.status"
          attach=".opportunity-header__status"
          searchAllowed
          @change="onStatusChange"
        />
      </div>
    </div>
    <div v-if="selectedButton && opportunity" class="opportunity-header__actions">
      <BaseNavigationButtons
        v-if="hasMultipleVisibleToggleButtons"
        :buttons="toggleButtons"
        :selected="selectedButton"
        isOrdered
        @button-clicked="switchOpportunityMode"
      >
        <template v-slot:default="{ button }">
          <div>
            {{ button.label }}
          </div>
        </template>
      </BaseNavigationButtons>
      <div class="opportunity-header__sync">
        <span v-for="(oppSync, index) in syncs" :key="index">
          <BaseEntitySync :entitySync="oppSync" @synchronize="onSynchronizeOpportunityClick(oppSync)" />
        </span>
      </div>
      <!-- Phones: its actions in a sheet, as a row's ⋯ (docs/page-layout.md). -->
      <div v-if="screenSize === 'phone'" class="opportunity-header__action">
        <BaseMoreActions :title="opportunity.name" :actions="headerActions" @select="onHeaderAction" />
      </div>
      <v-menu v-else v-model="actionMenu" absolute>
        <template v-slot:activator="{ on, attrs }">
          <div class="opportunity-header__action" @click.stop v-on="on" v-bind="attrs">
            <BaseIcon class="opportunity-header__action-icon" name="more-horizontal" />
          </div>
        </template>
        <div class="base-actions-menu">
          <ul class="base-actions-menu__ul">
            <li class="base-actions-menu__li" @click="onRenameOpportunity">
              <BaseIcon name="edit-2" />
              {{ $t('actions.rename') }}
            </li>
            <li class="base-actions-menu__li delete" @click="onDeleteOpportunity">
              <BaseIcon name="trash-2" />
              {{ $t('actions.delete') }}
            </li>
          </ul>
        </div>
      </v-menu>
    </div>

    <ConfirmationDialog
      ref="dialogDeleteOpportunity"
      :buttonText="$t('actions.delete')"
      @execute="executeOpportunityDelete"
    >
      <template slot="content">
        <div class="confirmation-dialog__content">
          <i18n path="page.opportunity.delete-opportunity-confirmation">
            <template v-slot:action>
              <span class="confirmation-dialog__action">{{ $t('actions.delete') }}</span>
            </template>
            <template v-slot:opportunityName v-if="opportunity">
              <span class="confirmation-dialog__opportunity">
                {{ opportunity.name }}
              </span>
            </template>
          </i18n>
          <div class="dialog__warning-notice">
            {{ $tc('page.opportunity.delete-opportunity-warning', 1) }}
          </div>
        </div>
      </template>
    </ConfirmationDialog>
    <SyncOpportunityDialog ref="syncOpportunityDialog" @confirm="synchronizeOpportunity" />
    <RenameOpportunityDialog ref="renameOpportunityDialog" />
  </div>
</template>

<script>
import BaseIcon from '@/components/base/BaseIcon';
import BaseMoreActions from '@/components/base/BaseMoreActions.vue';
import BaseEntitySync from '@/components/base/BaseEntitySync';
import BaseNavigationButtons from '@/components/base/BaseNavigationButtons';
import ConfirmationDialog from '@/components/page-admin-invites/ConfirmationDialog';
import RenameOpportunityDialog from '@/components/page-opportunity-details/RenameOpportunityDialog';
import SyncOpportunityDialog from '@/components/page-opportunity-details/SyncOpportunityDialog';

import { bus } from '@/main';
import {
  tenantHasDocumentsAccess,
  tenantHasFormFillingAccess,
  tenantHasGonogoAccess,
  tenantHasQuotesAccess,
  tenantHasSlipExtractionAccess,
  tenantHasTasksAccess,
} from '@/util/module-access-util';
import { isTenantUpsellRestricted } from '@/util/upsell-tenant-restriction-util';
import { mapActions, mapGetters } from 'vuex';
import { isXlsFile } from '@/util/document-file-util';
import OpportunityStatusType from '@/components/page-admin-opportunities/OpportunityStatusType.vue';
import BaseStatusMenu from '@/components/base/BaseStatusMenu.vue';
import { viewport } from '@/util/viewport';

export default {
  name: 'OpportunityHeader',
  components: {
    BaseEntitySync,
    BaseIcon,
    BaseMoreActions,
    BaseNavigationButtons,
    BaseStatusMenu,
    ConfirmationDialog,
    OpportunityStatusType,
    RenameOpportunityDialog,
    SyncOpportunityDialog,
  },
  data() {
    return {
      actionMenu: false,
      syncInterval: null,
    };
  },
  computed: {
    ...mapGetters('opportunity', ['opportunity']),
    ...mapGetters('opportunityStatuses', ['opportunityStatuses']),
    ...mapGetters('opportunitySyncProfiles', ['opportunitySyncProfiles']),
    ...mapGetters('opportunitySync', ['opportunitySyncs']),
    ...mapGetters('auth', ['currentUser']),
    screenSize() {
      return viewport.size;
    },
    headerActions() {
      return [
        { key: 'rename', label: this.$t('actions.rename'), icon: 'edit-2' },
        { key: 'delete', label: this.$t('actions.delete'), icon: 'trash-2', danger: true },
      ];
    },
    selectedButton() {
      return this.toggleButtons.find((button) => button.key === this.$route.name);
    },
    hasMultipleVisibleToggleButtons() {
      return this.toggleButtons.filter((button) => !button.hidden).length > 1;
    },
    hasDocuments() {
      return this.opportunity && this.opportunity.documents && this.opportunity.documents.length > 0;
    },
    firstPdfDocument() {
      return this.hasDocuments && this.opportunity.documents.find((document) => !isXlsFile(document));
    },
    toggleButtons() {
      return [
        {
          key: 'opportunity-details',
          label: this.$t('page.opportunity.mode.opportunity-details'),
          order: 1,
        },
        {
          key: 'opportunity-analysis',
          label: this.$t('page.opportunity.mode.analysis'),
          order: 3,
          locked: !tenantHasDocumentsAccess(),
          disabled: !this.firstPdfDocument || !tenantHasDocumentsAccess(),
          hidden:
            !tenantHasDocumentsAccess() && isTenantUpsellRestricted(this.currentUser && this.currentUser.tenantId),
          tooltip: !tenantHasDocumentsAccess()
            ? this.$t('page.opportunity.access.documents')
            : !this.firstPdfDocument
            ? this.$t('page.opportunity.access.analysis')
            : '',
        },
        {
          key: 'opportunity-go-nogo',
          label: this.$t('page.opportunity.mode.go-nogo'),
          order: 4,
          hidden: !tenantHasGonogoAccess() && isTenantUpsellRestricted(this.currentUser && this.currentUser.tenantId),
          disabled: !tenantHasGonogoAccess(),
          locked: !tenantHasGonogoAccess(),
          tooltip: !tenantHasGonogoAccess() ? this.$t('page.opportunity.access.go-nogo') : '',
        },
        {
          key: 'opportunity-forms',
          label: this.$t('page.opportunity.mode.forms'),
          order: 3.5,
          locked: !tenantHasDocumentsAccess() || !tenantHasFormFillingAccess(),
          disabled: !tenantHasDocumentsAccess() || !tenantHasFormFillingAccess(),
          hidden:
            (!tenantHasDocumentsAccess() || !tenantHasFormFillingAccess()) &&
            isTenantUpsellRestricted(this.currentUser && this.currentUser.tenantId),
          tooltip: !tenantHasDocumentsAccess()
            ? this.$t('page.opportunity.access.documents')
            : !tenantHasFormFillingAccess()
            ? this.$t('page.opportunity.access.forms')
            : '',
        },
        {
          key: 'opportunity-estimation',
          label: this.$t('page.opportunity.mode.estimation'),
          order: 5,
          hidden: !tenantHasSlipExtractionAccess(),
          disabled: !this.hasDocuments,
          tooltip: !tenantHasSlipExtractionAccess()
            ? this.$t('page.opportunity.access.estimation')
            : !this.hasDocuments
            ? this.$t('page.opportunity.access.has-no-documents')
            : '',
        },
        {
          key: 'opportunity-quotes',
          label: this.$t('page.opportunity.mode.quotes'),
          hidden: !tenantHasQuotesAccess() && isTenantUpsellRestricted(this.currentUser && this.currentUser.tenantId),
          order: 6,
          disabled: !tenantHasQuotesAccess(),
          locked: !tenantHasQuotesAccess(),
          tooltip: !tenantHasQuotesAccess() ? this.$t('page.opportunity.access.quotes') : '',
        },
        {
          key: 'opportunity-tasks',
          label: this.$t('page.opportunity.mode.tasks'),
          order: 7,
          hidden: !tenantHasTasksAccess() && isTenantUpsellRestricted(this.currentUser && this.currentUser.tenantId),
          disabled: !tenantHasTasksAccess(),
          locked: !tenantHasTasksAccess(),
          tooltip: !tenantHasTasksAccess() ? this.$t('page.opportunity.access.tasks') : '',
        },
      ];
    },
    activeSyncProfiles() {
      return this.opportunitySyncProfiles.filter((profile) => profile.active);
    },
    syncs() {
      // The opportunity is not loaded yet when the header is created, and it is reset when leaving the page.
      if (!this.opportunity) return [];
      return this.activeSyncProfiles.map((profile) => {
        const syncObj = this.opportunitySyncs.find(
          (oppSync) => oppSync.profileId === profile.id && oppSync.opportunityId === this.opportunity.id,
        );
        return { profile, sync: syncObj };
      });
    },
    // Scoped to this opportunity: the store also holds the opportunities page's entries.
    hasInProgressSyncs() {
      return this.syncs.some((oppSync) => oppSync.sync && oppSync.sync.status === 'IN_PROGRESS');
    },
  },
  methods: {
    ...mapActions('opportunity', ['deleteOpportunity', 'editOpportunity']),
    ...mapActions('opportunityStatuses', ['getOpportunityStatuses']),
    ...mapActions('opportunitySyncProfiles', ['getOpportunitySyncProfiles']),
    ...mapActions('opportunitySync', ['getOpportunitySyncs', 'syncOpportunity']),
    tenantHasDocumentsAccess,
    tenantHasGonogoAccess,
    tenantHasQuotesAccess,
    tenantHasSlipExtractionAccess,
    // The list brings this row back into view, however the reader returns to it (a phone's back
    // gesture skips the back button).
    announceViewed() {
      if (this.opportunity) bus.$emit('last-viewed-opportunity-id', this.opportunity.id);
    },
    backToOpportunities() {
      const savedUrl = localStorage.getItem('lastOpportunitiesUrl');
      bus.$emit('last-viewed-opportunity-id', this.opportunity.id);
      if (savedUrl) {
        this.$router.push(savedUrl);
      } else {
        this.$router.push({ name: 'opportunities' });
      }
    },
    switchOpportunityMode(button) {
      const key = button.key;

      if (key === 'opportunity-details') {
        this.$router.push({ name: 'opportunity-details', params: { id: this.opportunity.id } });
        return;
      }

      if (key === 'tender-details' && this.opportunity.tenderId) {
        this.$router.push({ name: 'tender-details', params: { id: this.opportunity.tenderId } });
        return;
      }

      if (key === 'opportunity-analysis' && tenantHasDocumentsAccess() && this.firstPdfDocument) {
        this.$router.push({
          name: 'opportunity-analysis',
          params: {
            opportunityId: this.opportunity.id,
            documentId: this.firstPdfDocument.id,
          },
        });
        return;
      }

      if (key === 'opportunity-forms' && tenantHasDocumentsAccess() && tenantHasFormFillingAccess()) {
        this.$router.push({
          name: 'opportunity-forms',
          params: {
            opportunityId: this.opportunity.id,
          },
        });
        return;
      }

      if (key === 'opportunity-go-nogo' && tenantHasGonogoAccess()) {
        this.$router.push({
          name: 'opportunity-go-nogo',
          params: {
            opportunityId: this.opportunity.id,
          },
        });
      }

      if (key === 'opportunity-estimation' && tenantHasSlipExtractionAccess() && tenantHasDocumentsAccess()) {
        this.$router.push({
          name: 'opportunity-estimation',
          params: {
            opportunityId: this.opportunity.id,
          },
        });
      }

      if (key === 'opportunity-quotes' && tenantHasQuotesAccess()) {
        this.$router.push({
          name: 'opportunity-quotes',
          params: {
            opportunityId: this.opportunity.id,
          },
        });
      }

      if (key === 'opportunity-tasks' && tenantHasTasksAccess()) {
        this.$router.push({
          name: 'opportunity-tasks',
          params: {
            opportunityId: this.opportunity.id,
          },
        });
      }
    },
    onStatusChange(newStatus) {
      this.$refs.opportunityStatusMenu.setLoading(true);
      const body = {
        name: this.opportunity.name,
        evaluationGridTemplateId: null,
        statusId: newStatus ? newStatus.id : null,
        startDate: this.opportunity.startDate,
        endDate: this.opportunity.endDate,
        source: this.opportunity.source,
        emitter: this.opportunity.emitter,
        notes: this.opportunity.notes,
        version: this.opportunity.version,
      };
      return this.editOpportunity({
        opportunityId: this.opportunity.id,
        opportunity: body,
      }).finally(() => this.$refs.opportunityStatusMenu.setLoading(false));
    },
    onHeaderAction(key) {
      if (key === 'rename') this.onRenameOpportunity();
      else this.onDeleteOpportunity();
    },
    onDeleteOpportunity() {
      this.$refs.dialogDeleteOpportunity.openDialog();
    },
    onRenameOpportunity() {
      this.$refs.renameOpportunityDialog.openDialog(this.opportunity);
    },
    executeOpportunityDelete() {
      this.deleteOpportunity({ opportunity: this.opportunity })
        .then(() => {
          this.$toast.success(this.$t('page.opportunity-details.opportunity-manipulations.delete-success'));
          this.$router.push({ name: 'opportunities' });
        })
        .catch((error) => {
          this.$toast.error(this.$t('page.opportunity-details.opportunity-manipulations.delete-fail'));
        });
    },
    fetchOpportunitySyncs() {
      let profileIds = this.activeSyncProfiles.map((profile) => profile.id);
      if (!profileIds.length || !this.opportunity) return Promise.resolve();

      return this.getOpportunitySyncs({ profileIds, opportunityIds: [this.opportunity.id] });
    },
    onSynchronizeOpportunityClick(oppSync) {
      // Show the dialog for new syncs or failed syncs (let the user correct field values before retrying)
      if (!oppSync.sync || oppSync.sync.status === 'FAILED') {
        this.$refs.syncOpportunityDialog.openDialog(this.opportunity, oppSync);
      } else {
        // Already synced: re-sync directly; the backend reuses the last submitted field values.
        this.synchronizeOpportunity(oppSync, {});
      }
    },
    synchronizeOpportunity(oppSync, fieldValues) {
      if (oppSync.sync && oppSync.sync.status && oppSync.sync.status === 'IN_PROGRESS') {
        this.$toast.warning(this.$t('opportunity-sync.statuses.in-progress', { date: oppSync.sync.lastSyncDate }));
        return;
      }
      this.syncOpportunity({
        profileId: oppSync.profile.id,
        opportunityId: this.opportunity.id,
        fieldValues,
      }).then(() => {
        this.$toast.success(this.$t('opportunity-sync.success'));
        this.fetchOpportunitySyncs();
      });
    },
    handleInProgressSyncs() {
      clearInterval(this.syncInterval);
      if (this.hasInProgressSyncs) {
        this.syncInterval = setInterval(() => {
          this.fetchOpportunitySyncs();
        }, 10000);
      }
    },
    // On a narrow screen the tabs scroll sideways; the current one must be in view, or the
    // last tabs would open the page with the reader's tab hidden off the edge.
    revealSelectedTab() {
      this.$nextTick(() => {
        const strip = this.$el.querySelector('.base-navigation-buttons');
        const selected = strip && strip.querySelector('.is-selected');
        if (!selected || strip.scrollWidth <= strip.clientWidth) return;
        strip.scrollLeft = selected.offsetLeft - (strip.clientWidth - selected.offsetWidth) / 2;
      });
    },
  },
  watch: {
    opportunity() {
      // The tabs appear with the opportunity.
      this.revealSelectedTab();
      this.announceViewed();
      this.getOpportunitySyncProfiles({}).then(() => {
        this.fetchOpportunitySyncs().then(() => {
          this.handleInProgressSyncs();
        });
      });
    },
    hasInProgressSyncs() {
      this.handleInProgressSyncs();
    },
    // A rotation can make the tabs scroll, or change how much of them shows.
    screenSize() {
      this.revealSelectedTab();
    },
  },
  created() {
    this.getOpportunityStatuses();
  },
  mounted() {
    this.revealSelectedTab();
    this.announceViewed();
  },
  beforeDestroy() {
    clearInterval(this.syncInterval);
  },
};
</script>

<style lang="scss">
.opportunity-header {
  background: $color-white;
  border-bottom: 1px solid $color-border;
  display: flex;
  height: $header-height;
  align-items: center;
  position: sticky;
  top: $menu-top-bar-height;
  user-select: none;
  width: 100%;
  z-index: $header-index;

  &__back {
    align-items: center;
    border-right: 1px solid $color-border;
    color: $color-normal;
    cursor: pointer;
    display: flex;
    height: 100%;
    justify-content: center;
    line-height: 1;
    outline: none;
    padding: 16px 32px;
    text-align: left;
    text-decoration: none;
    flex-grow: 0;
    min-width: max-content;

    &:hover,
    &:active,
    &:focus {
      color: $color-accent;
    }

    &-icon {
      color: $color-neutral;
      margin-right: 8px;
    }

    &-text {
      font-size: 14px;
      font-weight: bold;
      text-transform: uppercase;
    }
  }

  &__name {
    display: flex;
    gap: 12px;
    align-items: center;
    padding-left: 32px;
    min-width: 0;
    flex: 1;
  }

  &__status {
    display: flex;
    align-items: center;
    position: relative;
    flex-shrink: 0;
    overflow: visible;
    margin-left: auto;

    .v-menu__content.base-menu {
      position: absolute !important;
      top: 100% !important;
      left: auto !important;
      right: 0;
    }
  }

  &__title {
    align-items: center;
    font-weight: bold;
    user-select: text;
    // Makes the title take only three lines max
    display: -webkit-box;
    overflow: hidden;
    word-wrap: break-word;
    text-overflow: ellipsis;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
  }

  &__sync {
    margin-left: 8px;
    display: flex;
    flex-direction: row;
  }

  &__actions {
    align-items: center;
    display: flex;
    justify-content: flex-end;
    flex-grow: 0;
    min-width: max-content;
    padding: 0 32px;
    margin-left: auto;
  }

  &__action {
    display: flex;
    align-items: center;
    margin-left: 12px;
    cursor: pointer;

    &-icon {
      color: $color-normal-pale;
      transition: color 200ms ease;

      &:hover {
        color: $color-normal;
        transition: color 200ms ease;
      }
    }
  }

  // Tablets and phones: two rows. The opportunity (back, status, title, status menu, "⋯")
  // on the first; the tabs, scrolling sideways when they don't fit, and the syncs on the second.
  @include screen-below('laptop') {
    flex-wrap: wrap;
    align-content: flex-start;
    height: $opportunity-header-compact-height;

    // Breaks the line between the two rows, and draws the line between them.
    &::after {
      content: '';
      order: 2;
      flex-basis: 100%;
      height: 0;
      border-top: 1px solid $color-border;
    }

    &__back {
      height: 56px;
      padding: 0 16px;

      .tender-header__back-text {
        display: none;
      }

      .tender-header__back-icon {
        margin: 0;
      }
    }

    &__name {
      height: 56px;
      gap: 8px;
      padding-left: 12px;
    }

    &__title {
      -webkit-line-clamp: 1;
    }

    // Its children join the rows above and below.
    &__actions {
      display: contents;
    }

    &__action {
      order: 1;
      height: 56px;
      margin: 0 16px 0 8px;
    }

    .base-navigation-buttons {
      position: relative;
      order: 3;
      // From 0, so the syncs keep their place on this row; the strip then scrolls.
      flex: 1 1 0;
      min-width: 0;
      height: 48px;
      padding: 6px 12px;
      overflow-x: auto;
      border: 0;
      border-radius: 0;
      scrollbar-width: none;

      &::-webkit-scrollbar {
        display: none;
      }
    }

    .base-navigation-buttons__btn {
      flex: none;
      white-space: nowrap;
    }

    &__sync {
      order: 4;
      height: 48px;
      align-items: center;
      margin: 0 8px 0 0;
    }
  }

  @include top-bar-follower;

  @include screen-below('tablet') {
    &__back {
      padding: 0 14px;
    }

    &__title {
      -webkit-line-clamp: 2;
      font-size: 14px;
      line-height: 1.25;
    }
  }
}
</style>
